import React, { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { SearchInput } from 'components/atoms/input';
import { PageLoading } from 'components/atoms/loading';
import { FilterBar } from 'components/molecules/filter-bar';
import { Page } from 'components/molecules/page';
import { HeaderWithIconTitle } from 'components/molecules/page/page';
import { FilterBarWrapper } from 'components/organisms/archived-projects/archived-projects-list/styles';
import { TemplateObjectType } from 'data/templates/api';
import {
  Translate,
  TranslationKey,
  useTranslation,
} from 'hooks/use-translation';
import { TemplateEmptyState } from './empty-state';
import { NewTemplateCta } from './new-template-cta';
import { TemplateTableList } from './table/table';
import { TemplateListProp } from './types';

export const INTERMEDIATE_TEMPLATE_ID = -999;

const Header = styled.span`
  align-items: center;
  display: flex;
  flex: 1;
`;
const PageContent = styled(Page.Content)`
  padding: var(--layout-01);
  padding-right: var(--spacing-06);
  padding-left: var(--left-spacing);
`;

const FilterBarWrapperWithoutPadding = styled(FilterBarWrapper)`
  padding: 0;
  margin-bottom: var(--layout-02);
  box-shadow: none;
`;

const SearchInputWithMargin = styled(SearchInput)`
  margin-right: var(--spacing-05);
  .ant-btn {
    border: none;
  }
`;

export const TemplateListSection: FC<{
  loading: boolean;
  objectType: TemplateObjectType;
  route: string;
  translationKey: TranslationKey;
  isCreating?: boolean;
  onCreateNew?: () => void;
  onDelete: (id, objectType: TemplateObjectType) => void;
  onDuplicate?: (id, objectType: TemplateObjectType) => void;
  templates: TemplateListProp[];
  icon?: ReactNode;
  customTemplateCardRenderer?: ({
    template,
    isLoading,
    translate,
  }: {
    template: TemplateListProp;
    isLoading?: boolean;
    translate: Translate;
    onDelete?: (id, objectType: TemplateObjectType) => void;
    onDuplicate?: (id, objectType: TemplateObjectType) => void;
  }) => JSX.Element;
}> = ({
  loading,
  objectType,
  route,
  translationKey,
  templates,
  isCreating,
  onDelete,
  onCreateNew,
  onDuplicate,
  icon,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [filteredTemplates, setFilteredTemplates] = useState<
    TemplateListProp[]
  >([]);
  const searchPlaceholderText = useTranslation('template.search_placeholder');
  const onChange = (e) => setSearchValue(e.target.value);

  const filterFromSearchValue = useCallback(
    (templatesList: TemplateListProp[]) => {
      return templatesList?.filter((template) =>
        !searchValue.trim()
          ? true
          : template.templateName
              ?.toLowerCase()
              ?.includes(searchValue?.toLowerCase()),
      );
    },
    [searchValue],
  );

  useEffect(() => {
    setFilteredTemplates(filterFromSearchValue(templates));
  }, [filterFromSearchValue, templates]);

  const docTitle = useTranslation(translationKey);

  return (
    <Page>
      <Helmet>{docTitle}</Helmet>
      <Page.Header
        $white
        title={
          <Header>
            <HeaderWithIconTitle icon={icon} title={docTitle} />
          </Header>
        }
      />

      <PageContent>
        {loading ? (
          <PageLoading />
        ) : templates.length > 0 ? (
          <>
            <FilterBarWrapperWithoutPadding>
              <FilterBar.Primary />
              <FilterBar.Secondary>
                <SearchInputWithMargin
                  disabled={loading}
                  placeholder={searchPlaceholderText}
                  size="small"
                  onChange={onChange}
                  value={searchValue}
                  direction="right"
                />
                <NewTemplateCta
                  onClick={onCreateNew}
                  route={`${route}/new`}
                  isCreating={isCreating}
                  translationKey={translationKey}
                >
                  <FormattedMessage id={`${translationKey}.create`} />
                </NewTemplateCta>
              </FilterBar.Secondary>
            </FilterBarWrapperWithoutPadding>
            <TemplateTableList
              templateData={filteredTemplates}
              objectType={objectType}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
              route={route}
            />
          </>
        ) : (
          <TemplateEmptyState
            route={route}
            translationKey={translationKey}
            onCreateNew={onCreateNew}
            objectType={objectType}
          />
        )}
      </PageContent>
    </Page>
  );
};
